/* ECHARME – Store locator: lista + mappa Leaflet, ricerca locale/geocodifica, vicino a me. */
(() => {
  if (customElements.get('echarme-store-locator')) return;

  const LEAFLET = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/';
  const CLUSTER = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/';

  /** @type {Promise<void> | null} */
  let leafletPromise = null;

  const loadCss = (href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.append(link);
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.append(script);
    });

  const loadLeaflet = () => {
    if (!leafletPromise) {
      loadCss(`${LEAFLET}leaflet.min.css`);
      loadCss(`${CLUSTER}MarkerCluster.min.css`);
      leafletPromise = (window.L ? Promise.resolve() : loadScript(`${LEAFLET}leaflet.min.js`)).then(() =>
        window.L.markerClusterGroup ? undefined : loadScript(`${CLUSTER}leaflet.markercluster.min.js`)
      );
    }
    return leafletPromise;
  };

  const normalize = (value) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/['’.]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const distanceKm = (lat1, lng1, lat2, lng2) => {
    const rad = (x) => (x * Math.PI) / 180;
    const a =
      Math.sin(rad(lat2 - lat1) / 2) ** 2 +
      Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(a));
  };

  const formatKm = (km) => (km < 10 ? `${km.toFixed(1).replace('.', ',')} km` : `${Math.round(km)} km`);

  class EcharmeStoreLocator extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-list]');
      this.cards = [...this.querySelectorAll('[data-store]')];
      this.stores = this.cards.map((card) => ({
        card,
        region: card.dataset.region,
        lat: parseFloat(card.dataset.lat),
        lng: parseFloat(card.dataset.lng),
        services: (card.dataset.services || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        search: normalize(card.dataset.search || ''),
        distanceEl: card.querySelector('[data-distance]'),
        marker: null,
      }));
      this.status = this.querySelector('[data-status]');
      this.empty = this.querySelector('[data-empty]');
      this.body = this.querySelector('.esl__body');
      this.initialStatus = this.status?.textContent || '';
      this.activeServices = new Set();
      this.origin = null;

      this.querySelector('[data-search-form]')?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.search(this.querySelector('[data-search-input]').value);
      });
      this.querySelector('[data-search-input]')?.addEventListener('input', (e) => {
        if (!e.target.value.trim()) this.reset();
      });
      this.querySelector('[data-near-me]')?.addEventListener('click', () => this.nearMe());
      this.querySelector('[data-region-select]')?.addEventListener('change', (e) => {
        this.region = e.target.value;
        this.apply();
        this.fitVisible();
      });
      this.querySelectorAll('[data-view]').forEach((tab) =>
        tab.addEventListener('click', () => this.setView(tab.dataset.view))
      );

      this.buildServiceChips();

      this.cards.forEach((card, index) => {
        const open = () => this.focusStore(this.stores[index], true);
        card.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          open();
        });
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') open();
        });
      });

      // La mappa si carica solo quando la sezione si avvicina al viewport
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          this.initMap();
        },
        { rootMargin: '300px' }
      );
      io.observe(this);

      const params = new URLSearchParams(location.search);
      const q = params.get('negozio') || params.get('q');
      if (q) {
        this.querySelector('[data-search-input]').value = q;
        this.search(q);
      }
    }

    buildServiceChips() {
      const wrap = this.querySelector('[data-services-filter]');
      if (!wrap) return;
      const all = [...new Set(this.stores.flatMap((s) => s.services))].sort((a, b) => a.localeCompare(b));
      all.forEach((service) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'esl__chip';
        chip.textContent = service;
        chip.setAttribute('aria-pressed', 'false');
        chip.addEventListener('click', () => {
          const on = !this.activeServices.has(service);
          on ? this.activeServices.add(service) : this.activeServices.delete(service);
          chip.setAttribute('aria-pressed', String(on));
          this.apply();
          this.fitVisible();
        });
        wrap.append(chip);
      });
    }

    async initMap() {
      const el = this.querySelector('[data-map]');
      if (!el || this.map) return;
      try {
        await loadLeaflet();
      } catch {
        el.hidden = true;
        return;
      }
      const L = window.L;
      if (this.dataset.grayscale === 'true') el.classList.add('is-grayscale');
      this.map = L.map(el, { scrollWheelZoom: false, zoomControl: true }).setView([42.3, 12.6], 6);
      L.tileLayer(this.dataset.tileUrl || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: this.dataset.tileAttribution || '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(this.map);
      this.map.on('focus', () => this.map.scrollWheelZoom.enable());
      this.map.on('blur', () => this.map.scrollWheelZoom.disable());

      const icon = L.divIcon({ className: '', html: '<span class="esl-marker"></span>', iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28] });
      this.cluster = L.markerClusterGroup
        ? L.markerClusterGroup({
            showCoverageOnHover: false,
            maxClusterRadius: 45,
            iconCreateFunction: (c) =>
              L.divIcon({ className: '', html: `<span class="esl-cluster">${c.getChildCount()}</span>`, iconSize: [40, 40] }),
          })
        : L.layerGroup();

      this.stores.forEach((store) => {
        if (Number.isNaN(store.lat) || Number.isNaN(store.lng)) return;
        store.marker = L.marker([store.lat, store.lng], { icon, keyboard: false });
        store.marker.bindPopup(() => this.popupHtml(store));
        store.marker.on('click', () => this.focusStore(store, false));
        this.cluster.addLayer(store.marker);
      });
      this.map.addLayer(this.cluster);
      this.apply();
      this.fitVisible();
    }

    popupHtml(store) {
      const card = store.card;
      const name = card.querySelector('.esl-card__name')?.textContent || '';
      const centre = card.querySelector('.esl-card__centre')?.textContent || '';
      const address = card.querySelector('.esl-card__address')?.innerHTML || '';
      const links = [...card.querySelectorAll('.esl-card__actions a')].map((a) => a.outerHTML).join(' · ');
      return `<div class="esl-popup"><span class="esl-popup__name">${name}</span>${centre ? `${centre}<br>` : ''}${address}<br>${links}</div>`;
    }

    setView(view) {
      this.body.dataset.viewCurrent = view;
      this.querySelectorAll('[data-view]').forEach((tab) => {
        const on = tab.dataset.view === view;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', String(on));
      });
      if (view === 'map') {
        this.initMap().then(() => {
          this.map?.invalidateSize();
          this.fitVisible();
        });
      }
    }

    matches(store) {
      if (this.region && store.region !== this.region) return false;
      if (this.activeServices.size && ![...this.activeServices].every((s) => store.services.includes(s))) return false;
      // ogni parola deve iniziare una parola dei dati (es. "napoli" non trova "Etnapolis")
      if (this.textFilter && !this.textFilter.every((t) => ` ${store.search}`.includes(` ${t}`))) return false;
      return true;
    }

    apply() {
      let visible = 0;
      const ordered = [...this.stores];
      if (this.origin) {
        ordered.forEach((s) => {
          s.distance = Number.isNaN(s.lat) ? Infinity : distanceKm(this.origin.lat, this.origin.lng, s.lat, s.lng);
        });
        ordered.sort((a, b) => a.distance - b.distance);
      }
      ordered.forEach((store) => {
        const show = this.matches(store);
        store.card.hidden = !show;
        if (show) visible++;
        if (store.distanceEl) {
          store.distanceEl.hidden = !this.origin || !Number.isFinite(store.distance);
          if (this.origin && Number.isFinite(store.distance)) store.distanceEl.textContent = formatKm(store.distance);
        }
        if (store.marker && this.cluster) {
          const has = this.cluster.hasLayer(store.marker);
          if (show && !has) this.cluster.addLayer(store.marker);
          if (!show && has) this.cluster.removeLayer(store.marker);
        }
      });
      if (this.origin) ordered.forEach((s) => this.list.append(s.card));
      else this.stores.forEach((s) => this.list.append(s.card));
      if (this.empty) this.empty.hidden = visible > 0;
      return visible;
    }

    fitVisible() {
      if (!this.map) return;
      const L = window.L;
      const visible = this.stores.filter((s) => s.marker && !s.card.hidden);
      if (this.origin) {
        const nearest = visible.slice().sort((a, b) => a.distance - b.distance).slice(0, Number(this.dataset.nearest) || 5);
        const pts = nearest.map((s) => [s.lat, s.lng]).concat([[this.origin.lat, this.origin.lng]]);
        this.map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 13 });
        return;
      }
      if (!visible.length) return;
      this.map.fitBounds(L.latLngBounds(visible.map((s) => [s.lat, s.lng])), { padding: [30, 30], maxZoom: 13 });
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }

    reset() {
      this.origin = null;
      this.textFilter = null;
      this.meMarker?.remove();
      this.meMarker = null;
      this.apply();
      this.fitVisible();
      this.setStatus(this.initialStatus);
    }

    async search(query) {
      const q = normalize(query || '');
      if (!q) return this.reset();

      // 1. ricerca nei dati dei negozi (città, CAP, nome, provincia, regione)
      this.origin = null;
      this.meMarker?.remove();
      this.textFilter = q.split(' ');
      const found = this.apply();
      if (found) {
        const text = found === 1 ? this.dataset.textFoundOne : this.dataset.textFound;
        this.setStatus((text || '[count]').replace('[count]', found));
        this.fitVisible();
        this.cards.find((c) => !c.hidden)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
      }

      // 2. nessun negozio in quella località: geocodifica e ordina per distanza
      this.textFilter = null;
      this.setStatus(this.dataset.textSearching || '…');
      try {
        const geocode = async (params) => {
          const url = new URL('https://nominatim.openstreetmap.org/search');
          Object.entries({ ...params, format: 'json', limit: '5', countrycodes: 'it,sm', 'accept-language': 'it' }).forEach(
            ([k, v]) => url.searchParams.set(k, v)
          );
          const results = await (await fetch(url)).json();
          // preferisci il centro abitato al territorio (es. "Caserta" città, non la provincia)
          const places = ['city', 'town', 'village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'postcode', 'road'];
          const best = results.find((r) => places.includes(r.addresstype)) || results[0];
          return best ? [best] : [];
        };
        const trimmed = query.trim();
        let data = /^\d{5}$/.test(trimmed) ? await geocode({ postalcode: trimmed }) : await geocode({ q: trimmed });
        if (!data.length) throw new Error('not found');
        const place = data[0].display_name.split(',').slice(0, 2).join(',');
        this.setOrigin(parseFloat(data[0].lat), parseFloat(data[0].lon), place);
      } catch {
        this.apply();
        this.setStatus(this.dataset.textNoResults || '');
        if (this.empty) this.empty.hidden = false;
        this.stores.forEach((s) => (s.card.hidden = true));
      }
    }

    nearMe() {
      const button = this.querySelector('[data-near-me]');
      if (!navigator.geolocation) {
        this.setStatus(this.dataset.textGeoError || '');
        return;
      }
      button?.classList.add('is-loading');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          button?.classList.remove('is-loading');
          this.textFilter = null;
          const input = this.querySelector('[data-search-input]');
          if (input) input.value = '';
          this.setOrigin(pos.coords.latitude, pos.coords.longitude, null, true);
        },
        () => {
          button?.classList.remove('is-loading');
          this.setStatus(this.dataset.textGeoError || '');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    async setOrigin(lat, lng, place, isMe = false) {
      this.origin = { lat, lng };
      this.apply();
      this.setStatus(
        isMe ? this.dataset.textNearMe || '' : (this.dataset.textNear || '[place]').replace('[place]', place || '')
      );
      await this.initMap();
      if (this.map) {
        const L = window.L;
        this.meMarker?.remove();
        this.meMarker = L.marker([lat, lng], {
          icon: L.divIcon({ className: '', html: '<span class="esl-me"></span>', iconSize: [18, 18], iconAnchor: [9, 9] }),
          interactive: false,
        }).addTo(this.map);
        this.fitVisible();
      }
      this.list.scrollTo?.({ top: 0, behavior: 'smooth' });
    }

    focusStore(store, fromList) {
      this.cards.forEach((c) => c.classList.toggle('is-active', c === store.card));
      if (!fromList) store.card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (!store.marker || !this.map) {
        if (fromList && window.matchMedia('(max-width: 989px)').matches) return;
        this.initMap().then(() => store.marker && this.openMarker(store));
        return;
      }
      this.openMarker(store);
    }

    openMarker(store) {
      const open = () => {
        this.map.setView([store.lat, store.lng], Math.max(this.map.getZoom(), 14), { animate: true });
        store.marker.openPopup();
      };
      if (this.cluster.zoomToShowLayer) this.cluster.zoomToShowLayer(store.marker, open);
      else open();
    }
  }

  customElements.define('echarme-store-locator', EcharmeStoreLocator);
})();
